import { useMemo, useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useLingui } from '@lingui/react';
import { ChevronDown, Calendar, Type, Hash, Mail } from 'lucide-react';
import type { Table } from '@tanstack/react-table';
import type { FilterConfig, EditableCellConfig, FilterValue } from '../types';
import { FilterValuesPanel } from './FilterValuesPanel';

interface FilterOptionsListProps<T> {
  filterConfig: FilterConfig<T>;
  columnLabels: Record<string, string>;
  activeFilters: string[];
  searchQuery: string;
  hoveredOption: string | null;
  onOptionHover: (columnId: string | null) => void;
  columns: EditableCellConfig<T>[];
  onFilterApply: (columnId: string, value: FilterValue) => void;
  table: Table<T>;
  onClosePanel: () => void;
}

/**
 * Left column of the filter dropdown showing searchable list of filter options
 */
export function FilterOptionsList<T>({
  filterConfig,
  columnLabels,
  activeFilters,
  searchQuery,
  hoveredOption,
  onOptionHover,
  columns,
  onFilterApply,
  table,
  onClosePanel,
}: FilterOptionsListProps<T>) {
  const { i18n } = useLingui();
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Get icon for input type
  const getIconForInputType = (inputType?: string) => {
    switch (inputType) {
      case 'select':
        return ChevronDown;
      case 'date':
        return Calendar;
      case 'email':
        return Mail;
      case 'number':
        return Hash;
      case 'text':
      default:
        return Type;
    }
  };

  // Filter available options based on search query
  const filteredOptions = useMemo(() => {
    const options = Object.entries(filterConfig).map(([key]) => {
      const columnConfig = columns.find(col => col.accessorKey === key);
      const Icon = getIconForInputType(columnConfig?.inputType);
      
      return {
        id: key,
        label: columnLabels[key] || key,
        isActive: activeFilters.includes(key),
        icon: Icon,
        inputType: columnConfig?.inputType,
      };
    });

    if (!searchQuery) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(query)
    );
  }, [filterConfig, columnLabels, activeFilters, searchQuery, columns]);

  // Clear hoveredElement when hoveredOption changes to null
  // This ensures we don't have stale DOM references
  useEffect(() => {
    if (!hoveredOption) {
      setHoveredElement(null);
    }
  }, [hoveredOption]);

  return (
    <>
      <div ref={containerRef}>
        <div className="overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              {i18n._({ id: 'table.filter.noneFound', message: 'No filters found' })}
            </div>
          ) : (
            <ul className="py-1">
              {filteredOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <li
                    key={option.id}
                    onMouseEnter={(e) => {
                      onOptionHover(option.id);
                      setHoveredElement(e.currentTarget);
                    }}
                    className={cn(
                      "px-3 py-2 text-sm cursor-pointer transition-colors relative",
                      "hover:bg-accent hover:text-accent-foreground",
                      hoveredOption === option.id && "bg-accent text-accent-foreground",
                      option.isActive && "text-muted-foreground"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <div className="flex items-center justify-between flex-1">
                        <span>{option.label}</span>
                        {option.isActive && (
                          <span className="text-xs">Active</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      
      {/* Floating Filter Values Panel */}
      {hoveredOption && hoveredElement && (
        <FilterValuesPanel
          hoveredOption={hoveredOption}
          hoveredElement={hoveredElement}
          filterConfig={filterConfig}
          columnLabels={columnLabels}
          table={table}
          columns={columns}
          onFilterApply={onFilterApply}
          onClosePanel={onClosePanel}
        />
      )}
    </>
  );
}